// Lib
import React, { useState, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

// Utils
import { getDrawingViewBox } from '../utils/drawingCanvasUtils';

// Components
import DrawingPath from './DrawingPath';
import OutlinedDrawingPath from './OutlinedDrawingPath';
import DrawingEditorCanvasSvg from './DrawingEditorCanvasSvg';
import useDrawingPathHitTest from './inputManager/useDrawingPathHitTest';
import useDrawingCanvasModeInputLayerDelegate from './useDrawingCanvasModeInputLayerDelegate';

// Constants
import { DOM_POINTER_TYPES } from '../../../../utils/domConstants';

// Styles
import './DrawingEditorCanvasEraseMode.scss';

const DrawingEditorCanvasEraseMode = (props) => {
    const {
        paths,
        canvasScale,
        removePaths,
        debugUpdateAllStrokes,
        debugDrawingLastUpdateTime,
        externalInputLayerRef,
        inputLayerDelegateRef,
    } = props;

    const [hoveredPathId, setHoveredPathId] = useState();
    const erasedPathIdsMapRef = useRef({});

    const viewBox = getDrawingViewBox(props);

    const getHitPathIdFromClientPoint = useDrawingPathHitTest({
        inputLayerRef: externalInputLayerRef,
    });

    const erasePath = useCallback(
        (pathId) => {
            if (!pathId) return;
            if (erasedPathIdsMapRef.current[pathId]) return;

            erasedPathIdsMapRef.current[pathId] = true;
            removePaths([pathId]);
        },
        [removePaths],
    );

    const clearEraseState = useCallback(() => {
        erasedPathIdsMapRef.current = {};
        setHoveredPathId(undefined);
    }, []);

    const inputLayerDelegate = useMemo(
        () => ({
            onPointerDown: ({ event, clientPoint }) => {
                if (event.buttons !== 1) return;

                erasedPathIdsMapRef.current = {};
                setHoveredPathId(undefined);

                const hitPathId = getHitPathIdFromClientPoint(clientPoint);
                erasePath(hitPathId);
            },
            onPointerMove: ({ event, clientPoint }) => {
                const hitPathId = getHitPathIdFromClientPoint(clientPoint);

                if (event.buttons === 1) {
                    setHoveredPathId(undefined);
                    erasePath(hitPathId);
                    return;
                }

                const isHoverPointer =
                    event.pointerType === DOM_POINTER_TYPES.MOUSE || event.pointerType === DOM_POINTER_TYPES.PEN;

                setHoveredPathId(isHoverPointer ? hitPathId || undefined : undefined);
            },
            onPointerUp: clearEraseState,
            onPointerCancel: clearEraseState,
        }),
        [clearEraseState, erasePath, getHitPathIdFromClientPoint],
    );
    useDrawingCanvasModeInputLayerDelegate({ inputLayerDelegateRef, inputLayerDelegate });

    return (
        <>
            <DrawingEditorCanvasSvg viewBox={viewBox} className="DrawingEditorCanvasEraseMode">
                {paths.map((path) => {
                    const { id, ...rest } = path;

                    const isHovered = id === hoveredPathId;

                    return (
                        <React.Fragment key={id}>
                            <DrawingPath
                                data-path-id={id}
                                {...rest}
                                debugUpdateAllStrokes={debugUpdateAllStrokes}
                                debugDrawingLastUpdateTime={debugDrawingLastUpdateTime}
                            />
                            {isHovered && (
                                <OutlinedDrawingPath
                                    className="deletable-hovered"
                                    {...rest}
                                    canvasScale={canvasScale}
                                    debugUpdateAllStrokes={debugUpdateAllStrokes}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </DrawingEditorCanvasSvg>
        </>
    );
};

DrawingEditorCanvasEraseMode.propTypes = {
    paths: PropTypes.array,
    removePaths: PropTypes.func,

    canvasScale: PropTypes.number,
    viewBox: PropTypes.string,
    viewBoxX: PropTypes.number,
    viewBoxY: PropTypes.number,
    viewBoxWidth: PropTypes.number,
    viewBoxHeight: PropTypes.number,

    saveAndExitDrawingEditor: PropTypes.func,

    debugUpdateAllStrokes: PropTypes.bool,
    debugDrawingLastUpdateTime: PropTypes.number,
    externalInputLayerRef: PropTypes.object,
    inputLayerDelegateRef: PropTypes.object,
};

export default DrawingEditorCanvasEraseMode;
